import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { pipe, Option, Array as A } from 'effect';

import AddIcon from '@mui/icons-material/Add';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';

import {
  buildBranchOptions,
  findBranchOption,
  shouldOfferAdd,
  withManualBranch,
  type AddBranchOption,
} from '@/lib/bankBranch';

type BranchOption = { label: string; value: string };

// The branch registry lags real branch openings, so a missing branch can be
// added by hand. The form contract has no marker for this, hence the name.
const BRANCH_FIELD_NAME = 'branch_code';

const defaultFilter = createFilterOptions<BranchOption>();

type Params<T extends BranchOption> = {
  fieldName: string;
  options: T[];
  value: string;
  parentValue: string;
};

// DPM-901: a "+ Add" list entry for the branch field; pass-through for any other field.
const useBankBranchEntry = <T extends BranchOption>({ fieldName, options, value, parentValue }: Params<T>) => {
  const { formatMessage } = useIntl();
  const enabled = fieldName === BRANCH_FIELD_NAME;
  const hasParent = parentValue.length > 0;

  // A bank change remounts the field without reporting its emptied input, so
  // reset here; otherwise A → B → A would bring back the text typed under A.
  const [typed, setTyped] = useState({ parentValue, text: '' });
  if (typed.parentValue !== parentValue) setTyped({ parentValue, text: '' });
  const inputValue = typed.parentValue === parentValue ? typed.text : '';

  // Memoised: a new array identity rebuilds the virtualized listbox. No bank → no list, not even a manual branch.
  const displayOptions = useMemo(
    () => (enabled && hasParent ? withManualBranch(options, value) : options),
    [enabled, hasParent, options, value],
  );

  // '22' resolves to a listed '022'.
  const findSelected = (current: string) =>
    pipe(
      enabled
        ? findBranchOption(displayOptions, current)
        : A.findFirst(displayOptions, (option) => option.value === current),
      Option.getOrNull,
    );

  const filterOptions = useCallback(
    (list: BranchOption[], state: FilterOptionsState<BranchOption>) =>
      buildBranchOptions({
        filtered: defaultFilter(list, state),
        options: list,
        inputValue: hasParent ? state.inputValue : '',
        makeAddOption: (code): AddBranchOption => ({
          label: formatMessage({ id: 'bankBranch.addOption', defaultMessage: 'Add branch ({code})' }, { code }),
          value: code,
          isNew: true,
          startIcon: <AddIcon fontSize="small" />,
        }),
      }),
    [hasParent, formatMessage],
  );

  const noMatchText =
    enabled && hasParent && inputValue.trim()
      ? formatMessage({ id: 'validation.branchCode', defaultMessage: 'Branch code must be 1–3 digits' })
      : undefined;

  // "+ Add" is last, so Enter must not auto-pick the first label match ("12" → "120 - …").
  const offersAdd = enabled && hasParent && shouldOfferAdd(displayOptions, inputValue);

  const autocompleteProps = enabled
    ? {
        filterOptions,
        autoHighlight: !offersAdd,
        onInputChange: (_: unknown, text: string) => setTyped({ parentValue, text }),
      }
    : {};

  return { options: displayOptions, findSelected, autocompleteProps, noMatchText };
};

export default useBankBranchEntry;
